import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Search, Sun, Moon, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUIStore } from '@/stores/ui-store';
import { GlobalSearch } from '@/components/common/global-search';
import { LanguageSelector } from '@/components/common/language-selector';
import { useAuthStore } from '@/stores/auth-store';

export function Header() {
  const { t } = useTranslation();
  const { toggleSidebar, theme, toggleTheme } = useUIStore();
  const user = useAuthStore((state) => state.user);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  const isDark = theme === 'dark' || (theme === 'system' && document.documentElement.classList.contains('dark'));
  const sectionKey = location.pathname.includes('/settings')
    ? 'nav.settings'
    : location.pathname.includes('/security-report')
      ? 'nav.security'
      : location.pathname.includes('/authenticator')
        ? 'nav.authenticator'
        : location.pathname.includes('/generator')
          ? 'nav.generator'
          : location.pathname.includes('/vault') || location.pathname.includes('/item')
            ? 'nav.vaults'
            : location.pathname.includes('/trash')
              ? 'nav.trash'
              : location.pathname.includes('/downloads')
                ? 'nav.downloads'
                : 'nav.dashboard';

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-border/80 bg-card/95 backdrop-blur-md">
        <div className="flex h-[72px] items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={toggleSidebar}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="hidden min-w-0 md:block">
            <p className="data-label">OneckPass / {t('shell.workspace').toLowerCase()}</p>
            <h2 className="truncate text-base font-semibold tracking-[-0.01em]">{t(sectionKey)}</h2>
          </div>

          <div className="mx-auto w-full max-w-md md:ml-auto md:mr-0">
            <Button
              variant="outline"
              className="h-10 w-full justify-start rounded-xl border-border/80 bg-muted/55 px-3 text-muted-foreground shadow-none hover:border-primary/25 hover:bg-card"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="mr-2 h-4 w-4" />
              <span className="flex-1 text-left">{t('common.search')}...</span>
              <kbd className="hidden h-5 select-none items-center rounded-md border border-border bg-card px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
                ⌘ K
              </kbd>
            </Button>
          </div>

          <div className="flex items-center gap-1.5">
            <LanguageSelector />
            <Button
              variant="ghost"
              size="icon"
              className="rounded-xl text-muted-foreground"
              onClick={toggleTheme}
              title={isDark ? t('settings.theme.light') : t('settings.theme.dark')}
            >
              {isDark ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
            <div className="ml-1 hidden h-9 items-center gap-2.5 border-l border-border pl-3 sm:flex">
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {user?.email?.charAt(0).toUpperCase() || 'O'}
                <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-card bg-accent">
                  <ShieldCheck className="h-2 w-2 text-accent-foreground" />
                </span>
              </div>
              <span className="hidden max-w-32 truncate text-xs font-medium lg:block">{user?.email}</span>
            </div>
          </div>
        </div>
      </header>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
